import { Col, Row } from "react-bootstrap";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { artworkDetailSchema } from "@eaf/contracts";
import { ErrorState, LoadingState, PageFrame } from "@eaf/ui";
import { publicApi } from "../api/client";
import { ArtworkContent } from "../components/artwork-content";
import { Seo } from "../components/seo";

export function ArtworkPage() {
  const { id = "" } = useParams();
  const query = useQuery({
    queryKey: ["artwork", id],
    queryFn: () => publicApi(`/catalog/${encodeURIComponent(id)}`, artworkDetailSchema),
    enabled: Boolean(id)
  });
  if (query.isLoading)
    return (
      <PageFrame>
        <LoadingState label="Loading artwork" />
      </PageFrame>
    );
  if (query.isError || !query.data)
    return (
      <PageFrame>
        <ErrorState title="Artwork unavailable">
          <p>The requested artwork could not be found or loaded.</p>
          <Link to="/gallery">Return to the gallery</Link>
        </ErrorState>
      </PageFrame>
    );
  const artwork = query.data;
  const dimensions =
    artwork.width && artwork.height ? `${artwork.width} by ${artwork.height} inches` : null;
  const title = artwork.title || "Untitled";
  const pageUrl = `https://react.edmundalanfain.com/artwork/${encodeURIComponent(artwork.id)}`;
  const description = artwork.excerpt ?? "Artwork in the Edmund Alan Fain Collection.";
  return (
    <PageFrame>
      <Seo
        title={`${title} | Edmund Alan Fain`}
        description={description}
        image={artwork.image.xl}
        type="article"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "VisualArtwork",
          name: title,
          identifier: artwork.id,
          description,
          url: pageUrl,
          image: [artwork.image.xl, artwork.image.lg, artwork.image.md],
          creator: { "@type": "Person", name: "Edmund Alan Fain" },
          ...(artwork.medium ? { artMedium: artwork.medium } : {}),
          ...(artwork.paper ? { artworkSurface: artwork.paper } : {}),
          ...(artwork.width ? { width: artwork.width } : {}),
          ...(artwork.height ? { height: artwork.height } : {})
        }}
      />
      <Row className="artwork-detail g-5">
        <Col lg={7}>
          <img
            className="artwork-detail__image"
            src={artwork.image.xl}
            alt={artwork.title ?? "Untitled artwork"}
          />
        </Col>
        <Col lg={5}>
          <p className="eyebrow">Registration {artwork.id}</p>
          <h1>{title}</h1>
          <dl className="artwork-facts">
            <div>
              <dt>Sequence</dt>
              <dd>{artwork.seq ?? "—"}</dd>
            </div>
            <div>
              <dt>Medium</dt>
              <dd>
                {[artwork.paper, artwork.medium, artwork.orientation].filter(Boolean).join(" / ") ||
                  "—"}
              </dd>
            </div>
            <div>
              <dt>Dimensions</dt>
              <dd>{dimensions ?? "—"}</dd>
            </div>
            <div>
              <dt>Classification</dt>
              <dd>
                {[artwork.categoryPrimary, artwork.categorySecondary].filter(Boolean).join(" / ") ||
                  "—"}
              </dd>
            </div>
          </dl>
          {artwork.excerpt ? <p className="lead">{artwork.excerpt}</p> : null}
          {artwork.subjectTags ? (
            <Meta label="Subjects & motifs" value={artwork.subjectTags} />
          ) : null}
          {artwork.styleTraits ? <Meta label="Style traits" value={artwork.styleTraits} /> : null}
          {artwork.environmentalMoods ? (
            <Meta label="Atmosphere" value={artwork.environmentalMoods} />
          ) : null}
        </Col>
      </Row>
      {artwork.descriptionBlocks.length ? (
        <section className="artwork-description">
          <h2>About this work</h2>
          <ArtworkContent blocks={artwork.descriptionBlocks} />
        </section>
      ) : null}
    </PageFrame>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div className="artwork-meta">
      <strong>{label}</strong>
      <p>{value}</p>
    </div>
  );
}
